import { useState, type ReactNode } from "react";
import { formatCount } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { ArrowRight, Info } from "lucide-react";
import { Link, useInRouterContext } from "react-router-dom";

import type { SectionEnvelope } from "@/api/diagnostic";
import { Pill } from "@/components/brand/Pill";
import { cn } from "@/lib/utils";
import { DiagnosticSection, Section, type DiagnosticTabProps } from "../DiagnosticSection";
import { StackedBar } from "../primitives/StackedBar";
import {
  cleanExtensionCount,
  decisionDestination,
  wricefDecisions,
  type WricefDecisionFrame,
} from "../data/wricefDecisions";
import {
  activatedItems,
  activationVersion,
  evidenceCoverage,
  gapCategories,
  hasActiveScopeApproval,
  cleanCoreDestination,
  complexityHighlights,
  moduleCustomizations,
  processDestination,
  processFitNeedsRepublish,
  processFitRows,
  type ActivatedItem,
} from "../diagnosticData";
import {
  makeConfidenceMeta,
  makeCoverageTypeMeta,
  makeDecisionMeta,
  makeEvidenceQualityMeta,
  makeObservedSourceMeta,
  makeTierMeta,
} from "../diagnosticMeta";
import { NO_VALUE } from "../sectionState";
import { FIT_SEGMENTS, PROCESS_LABELS } from "../processFit/processFitLabels";
import { DECISION_BAR } from "./environment/WricefCard";
import { AreaGate } from "../projectAreas";

/**
 * Aderência e gaps — a aba 3 do Diagnóstico (DIAG.5 / #466).
 *
 * **Apresentacional por contrato**: recebe `sections` e `snapshots` por props e não
 * chama hook de dado, o que a torna testável sem mock (não há MSW no repo, por
 * decisão do S-059). O único estado é a seleção de tile do painel de evidência —
 * estado de UI, que não busca nada.
 *
 * O que esta aba defende, e por que ela existe: o painel de evidência responde
 * "**por que este scope item entrou**" diante do arquiteto SAP do cliente. Três
 * regras de honestidade governam o painel inteiro:
 *
 * 1. ⚠️ **`triggered_by` não é transação.** São nomes de tabela (`BSEG`),
 *    predicados de campo (`SKB1.XSPEB = X`) e gates de país (`country=BR`) — as
 *    três formas que `evaluate_predicate` produz (`engine.py:100-156`). O design
 *    pedia "transações observadas · N usuários · N execuções em 36 meses"; isso não
 *    existe, e rotular presença de tabela como uso medido seria a mentira que a
 *    Feature proíbe. Transação aparece **só** na linha de `observed_tcodes`.
 * 2. **Ausência de `observed_tcodes` não é lista vazia.** Quatro estados, quatro
 *    copies — ver {@link ObservedTcodes}.
 * 3. **Preço nulo é "a cotar", nunca R$ 0.** Um gap sem preço não entra na soma do
 *    rodapé, e a contagem dos que ficaram de fora viaja ao lado do total.
 *
 * Não renderiza, em lugar nenhum: coluna "Criticality" (não existe severidade em
 * nenhuma das duas listas de gap), o `count` do fingerprint (é binário por
 * construção — `max_rows=1` —, então não é volumetria), e card de custom code (o
 * design da aba 3 não tem um; dívida de superfície registrada na #461).
 */
export function DiagnosisAct({ sections, snapshots, validation }: DiagnosticTabProps) {
  return (
    <div className="grid grid-cols-1 gap-4">
      {/* A ordem é a do desenho aprovado (#879): indicadores, os dois pares de
          cards, e a barra única do destino dos processos no fim. */}
      {/* #1784: fora do SAP somem a aderência (domínio 9) e o código (Architect). */}
      <AreaGate area="adherence">
        <ComplexityCard sections={sections} snapshots={snapshots} />
      </AreaGate>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AreaGate area="adherence">
          <ProcessFitCard sections={sections} snapshots={snapshots} />
        </AreaGate>
        <AreaGate area="architect">
          <ModuleCustomizationsCard sections={sections} snapshots={snapshots} />
        </AreaGate>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AreaGate area="adherence">
          <GapCategoriesCard sections={sections} snapshots={snapshots} />
        </AreaGate>
        <AreaGate area="architect">
          <CleanCoreDestinationCard sections={sections} snapshots={snapshots} validation={validation} />
        </AreaGate>
      </div>

      <AreaGate area="adherence">
        <ProcessDestinationCard sections={sections} snapshots={snapshots} />
      </AreaGate>

      {/* Recolhido, e não card: ele não é parte da devolutiva. É o utilitário
          de quem apresenta, para quando o cliente perguntar por um scope item
          específico no meio da conversa. Decidido em 23/09/2026, junto com a
          saída dos outros quatro que o desenho não tem. */}
      <AreaGate area="adherence">
        <StandardItemsPanel sections={sections} snapshots={snapshots} />
      </AreaGate>
    </div>
  );
}

/** Rótulo de cada processo end-to-end. `other` é o balde de quem não mapeia. */

/** Os detalhes dos cards de processo do Ato 2 em `DIAGNOSTIC_DETAILS` (19/10/2026). */
const PROCESS_FIT_DETAIL = "process-fit";
const PROCESS_DESTINATION_DETAIL = "process-destination";

/** "Ver scope items": o detalhe do card, sem filtro. */
function SeeScopeItemsLink({ to }: { to: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See scope items")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/** A linha que abre o detalhe já filtrado; sem roteador, a mesma linha sem link. */
function DetailRowLink({
  to,
  testId,
  className,
  children,
}: {
  to: string | undefined;
  testId: string;
  className?: string;
  children: ReactNode;
}) {
  if (!to) {
    return (
      <div data-testid={testId} className={className}>
        {children}
      </div>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testId}
      className={cn(
        className ?? "block",
        "-mx-1 rounded px-1 py-0.5 hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]",
      )}
    >
      {children}
    </Link>
  );
}

/**
 * Aderência Fit-to-Standard por processo (#879).
 *
 * O eixo vem da curadoria em `ref_business_area_process`: nenhuma tabela do
 * schema tinha processo. Run anterior à curadoria não traz o eixo, e aí o card
 * diz isso em vez de desenhar barras vazias.
 *
 * A quinta linha do desenho, "Localização fiscal BR", **não sai deste eixo**:
 * ela é preocupação de país, transversal aos quatro processos. Declarar a
 * ausência é o que o critério da issue pede.
 */
function ProcessFitCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  const rows = processFitRows(sections);
  const labels = PROCESS_LABELS(t);
  const segments = FIT_SEGMENTS(t);
  // O card também é desenhado fora de um roteador (anexo, testes): ali ele conta
  // e não navega.
  const inRouter = useInRouterContext();

  return (
    <Section
      sections={sections}
      name="scope_activation"
      title={t("Fit-to-Standard by process")}
      snapshots={snapshots}
      showsSampling={false}
      actions={inRouter && rows.length > 0 ? <SeeScopeItemsLink to={PROCESS_FIT_DETAIL} /> : undefined}
    >
      <p className="mb-3 text-[11px] text-[#596b85]">
        {t("How much of the business the GROW standard already covers")}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-[#7b8799]">
          {processFitNeedsRepublish(sections)
            ? t("This axis needs to be recalculated: publish the calibration again.")
            : t("This run predates the process grouping; re-run to see this axis.")}
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.process}>
              <DetailRowLink
                to={inRouter ? `${PROCESS_FIT_DETAIL}?process=${row.process}` : undefined}
                testId={`process-row-${row.process}`}
              >
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate text-[#08254c]">
                  {labels[row.process] ?? row.process}
                </span>
                <span className="shrink-0 tabular-nums text-[#08254c]">
                  {row.fitPct !== undefined ? `${row.fitPct}%` : NO_VALUE}
                  <span className="ml-1 font-mono text-[10px] text-[#7b8799]">
                    {count(row.total)}
                  </span>
                </span>
              </div>
              <StackedBar
                className="mt-1"
                height="h-3"
                segments={segments.map((segment) => ({
                  key: segment.key,
                  label: segment.label,
                  className: segment.className,
                  value: row[segment.key],
                }))}
              />
              </DetailRowLink>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[11px] leading-snug text-[#7b8799]">
        {t(
          "Brazilian tax localisation is not a process: it cuts across all four, and this axis does not produce it.",
        )}
      </p>
    </Section>
  );
}

/**
 * O detalhe de cada número do card "Complexidade e decisões" (pedido do David em
 * 19/10/2026). Slug relativo ao Diagnóstico, como os outros detalhes.
 */
const COMPLEXITY_DETAILS: Record<string, string | undefined> = {
  "scope-items": "process-destination",
  gaps: "scope-gaps",
  engineering: "engineering-objects",
};

/** O número inteiro é o link: fora de um roteador (anexo, testes do ato) fica como era. */
function KpiLink({ to, children }: { to: string | undefined; children: ReactNode }) {
  const inRouter = useInRouterContext();
  if (!to || !inRouter) return <>{children}</>;
  return (
    <Link
      to={to}
      className="-m-1 block rounded p-1 hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
    >
      {children}
    </Link>
  );
}

/** Os três indicadores do topo do Ato 2 (#879). */
function ComplexityCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  const highlights = complexityHighlights(sections);

  const kpis = [
    {
      key: "scope-items",
      value: highlights.recommendedScopeItems,
      label: t("Recommended scope items"),
      hint: t("from the SAP Best Practices catalogue for GROW"),
    },
    {
      key: "gaps",
      value: highlights.gapsToDecide,
      label: t("Gaps to decide"),
      hint: t("outside the standard, they need a path chosen"),
    },
    {
      key: "engineering",
      value: highlights.objectsNeedingEngineering,
      label: t("Objects needing engineering"),
      // O denominador vai junto porque é ele que dá o sentido da frase: o
      // número só é uma boa notícia contra o total do catálogo.
      hint:
        highlights.catalogTotal !== undefined
          ? t("of {{total}}, only these need technical work", {
              total: count(highlights.catalogTotal),
            })
          : t("only these need technical work"),
    },
  ];

  return (
    <Section
      sections={sections}
      name="scope_activation"
      title={t("Complexity and decisions")}
      snapshots={snapshots}
      showsSampling={false}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {kpis.map((kpi) => (
          <KpiLink key={kpi.key} to={COMPLEXITY_DETAILS[kpi.key]}>
            <div data-testid={`complexity-${kpi.key}`}>
              <div className={MONO_LABEL}>{kpi.label}</div>
              <div className="mt-1 text-3xl font-semibold tabular-nums text-[#08254c]">
                {kpi.value === undefined ? NO_VALUE : count(kpi.value)}
              </div>
              <p className="mt-1 text-[11px] leading-snug text-[#596b85]">
                {kpi.hint}
              </p>
            </div>
          </KpiLink>
        ))}
      </div>
    </Section>
  );
}

/**
 * A barra do módulo abre o detalhe já filtrado por ele (pedido do David em
 * 19/10/2026). Fora de um roteador (anexo, testes sem rota) a linha fica como era.
 */
function ModuleRowLink({ code, children }: { code: string; children: ReactNode }) {
  const row = "flex items-center gap-3";
  if (!useInRouterContext()) return <div className={row}>{children}</div>;
  return (
    <Link
      to={`module-customizations?module=${encodeURIComponent(code)}`}
      className={cn(
        row,
        "rounded-sm hover:bg-[#f7f9fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]",
      )}
    >
      {children}
    </Link>
  );
}

/** "Ver objetos": o detalhe sem filtro, com todos os módulos. */
function ModuleObjectsLink() {
  const { t } = useTranslation();
  if (!useInRouterContext()) return null;
  return (
    <Link
      to="module-customizations"
      data-testid="module-objects-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See objects")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/** Onde o código Z se concentra. O agregado existia e nenhum card o lia (#879). */
function ModuleCustomizationsCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  const { rows, remainder, unidentified, identified, total } = moduleCustomizations(
    sections,
    { top: 5 },
  );
  // Agora `rows` só tem módulo nomeado, então o maior deles é a régua. Antes
  // o balde sem módulo estava na lista e era a maior linha de todas (3.939
  // contra 2.233 no appliance), o que fazia toda barra nomeada encolher
  // contra uma que não é módulo.
  const peak = rows.reduce((acc, row) => Math.max(acc, row.count), 0);

  // A frase do desenho ("SD e MM juntos respondem por quase metade") nomeia
  // módulo, e o denominador dela é o código com módulo **identificado**, e não
  // o catálogo inteiro (#963). Era essa a incoerência do card enquanto o balde
  // sem módulo era barra: a frase já o excluía, as barras não.
  const named = rows.slice(0, 2);
  const namedTotal = named.reduce((acc, row) => acc + row.count, 0);
  const namedShare =
    identified > 0 && named.length > 0
      ? Math.round((namedTotal / identified) * 100)
      : undefined;
  // A fração do catálogo que não tem módulo atribuível. Sobre o total, e não
  // sobre o identificado: a pergunta que ela responde é "quanto do meu código
  // ficou fora deste gráfico".
  const unidentifiedShare =
    total && total > 0 ? Math.round((unidentified / total) * 100) : undefined;

  return (
    <Section
      sections={sections}
      name="wricef_catalog"
      title={t("Customisations by module")}
      snapshots={snapshots}
      showsSampling={false}
      actions={<ModuleObjectsLink />}
    >
      {identified > 0 ? (
        // O subtítulo declara o que as barras repartem, e não o catálogo
        // inteiro: desde que o balde sem módulo saiu da régua, anunciar o
        // total faria as barras não fecharem com o número ao lado delas.
        <p className="mb-3 text-[11px] text-[#596b85]">
          {t("Where the Z code sits · {{identified}} with an identified module", {
            identified: count(identified),
          })}
        </p>
      ) : null}
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.module} data-testid={`module-row-${row.module}`} className="text-xs">
            <ModuleRowLink code={row.code}>
            {/* Os nomes de componente chegam a 30 caracteres ("Business
                Information Warehouse", "Válido para várias aplicações"), e em
                112px todos saíam cortados no mesmo ponto, indistinguíveis. O
                `title` mantém o nome inteiro ao passar o mouse. */}
            {/* Código sem rótulo não pode parecer nome de módulo. Ele fica
                como código, dizendo que é um — a decisão de nomear o balde
                pelo código continua (`application_component_labels.py`), o que
                sai é a ambiguidade de ele aparecer ao lado de dois nomes de
                verdade. */}
            <span
              className="w-44 shrink-0 truncate text-[#08254c]"
              title={row.labelled ? row.module : t("Code {{code}} (no label)", { code: row.module })}
            >
              {row.labelled ? (
                row.module
              ) : (
                <>
                  {row.module}{" "}
                  <span className="text-[10px] text-[#7b8799]">{t("(no label)")}</span>
                </>
              )}
            </span>
            <StackedBar
              className="flex-1"
              height="h-3"
              segments={[
                {
                  key: "n",
                  label: row.module,
                  className: "bg-[#4a7fd4]",
                  value: row.count,
                },
                {
                  key: "rest",
                  label: "",
                  className: "bg-transparent",
                  value: peak - row.count,
                },
              ]}
            />
            <span className="w-12 shrink-0 text-right tabular-nums text-[#08254c]">
              {count(row.count)}
            </span>
            </ModuleRowLink>
          </li>
        ))}
      </ul>
      {remainder > 0 ? (
        // Linha da lista, e não nota solta: o desenho a desenha como barra
        // cinza junto das outras, e fora da lista ela não se compara com
        // nenhuma. A cor a distingue de módulo nomeado.
        <div
          data-testid="module-remainder"
          className="mt-2 flex items-center gap-3 text-xs"
        >
          <span className="w-44 shrink-0 truncate text-[#7b8799]">
            {t("Other modules")}
          </span>
          <div className="h-3 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
            <div
              className="h-full rounded-sm bg-[#b2bccb]"
              style={{ width: `${Math.max(2, (remainder / Math.max(1, peak)) * 100)}%` }}
            />
          </div>
          <span className="w-12 shrink-0 text-right tabular-nums text-[#7b8799]">
            {count(remainder)}
          </span>
        </div>
      ) : null}
      {namedShare !== undefined && (
        <p
          data-testid="modules-insight"
          className="mt-3 text-[12px] leading-snug text-[#596b85]"
        >
          {t(
            "{{modules}} answer for {{percent}} of the identified custom code — it is where modernisation simplifies most.",
            {
              // Padrão traduzível, e não `join(", ")`: em português a lista de
              // dois pede "e", em inglês pede "and", e o desenho escreve
              // "SD e MM juntos respondem". Vírgula no meio da frase que o
              // vendedor fala em voz alta lê como enumeração truncada.
              // (`Intl.ListFormat` seria o certo, mas exige subir a `lib` do
              // tsconfig, que tem raio maior que o ganho aqui: são dois itens.)
              modules:
                named.length === 2
                  ? t("{{first}} and {{second}}", {
                      first: named[0].module,
                      second: named[1].module,
                    })
                  : named[0].module,
              percent: `${namedShare}%`,
            },
          )}
        </p>
      )}
      {unidentified > 0 && (
        // Nota, e não barra. O número continua dito por extenso — sumir com
        // ele seria afirmar uma concentração medida sobre o catálogo inteiro,
        // que é o oposto do que o card sabe. O que muda é o peso visual: a
        // resposta mais alta do gráfico volta a ser um módulo.
        <p
          data-testid="modules-unidentified"
          className="mt-2 text-[11px] leading-snug text-[#7b8799]"
        >
          {t(
            "{{count}} objects ({{percent}}) have no module to attribute: SAP only records the application component for programs, not for classes, function groups or interfaces.",
            {
              count: count(unidentified),
              percent:
                unidentifiedShare !== undefined ? `${unidentifiedShare}%` : "—",
            },
          )}
        </p>
      )}
    </Section>
  );
}

/** Rótulo e cor de cada destino, em vocabulário fechado. */
const DESTINATION_LABELS = (
  t: T,
): Record<string, { label: string; className: string }> => ({
  retire: { label: t("Discontinue (unused)"), className: "bg-[#b2bccb]" },
  // O valor que o catálogo grava é `standard_grow_equivalent`; `standard_grow`
  // fica como apelido porque runs antigas o usam. Sem os dois, a tela desenha
  // o código cru ao lado do número, que foi o que apareceu na validação.
  standard_grow_equivalent: {
    label: t("Covered by the standard"),
    className: "bg-[#27a155]",
  },
  standard_grow: {
    label: t("Covered by the standard"),
    className: "bg-[#27a155]",
  },
  requires_extension: {
    label: t("Needs extension"),
    className: "bg-[#4a7fd4]",
  },
  undecided: { label: t("Undecided"), className: "bg-[#ffc83d]" },
  activate: { label: t("GROW standard, direct"), className: "bg-[#27a155]" },
  // O veredito da foto aprovada chama-se `decision`; `needs_decision` é o do
  // run bruto do motor. Os dois coexistem porque o Clean Core ainda lê o
  // segundo, e apagá-lo deixaria aquele card sem rótulo.
  decision: { label: t("Assisted decision"), className: "bg-[#ffc83d]" },
  needs_decision: { label: t("Assisted decision"), className: "bg-[#ffc83d]" },
  needs_review: {
    label: t("Needs dedicated analysis"),
    className: "bg-[#f27325]",
  },
  skip: { label: t("Out of scope"), className: "bg-[#b2bccb]" },
});

/**
 * Barra única com contagem e percentual ao lado.
 *
 * `pct` ausente não vira zero: quando o denominador não foi declarado, a linha
 * mostra só a contagem. Percentual sem denominador é afirmação que o dado não
 * sustenta, e é critério da issue.
 */
function DestinationBody({
  subtitle,
  destination,
  testid,
  linkFor,
  labels: labelsOverride,
}: {
  subtitle?: string;
  destination: ReturnType<typeof cleanCoreDestination>;
  testid: string;
  /** O detalhe que cada fatia da legenda abre; sem ele a legenda só conta. */
  linkFor?: (key: string) => string | undefined;
  /** Rótulo e cor por fatia, quando as fatias não são destinos (#1712: decisões). */
  labels?: Record<string, { label: string; className: string }>;
}) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  const labels = labelsOverride ?? DESTINATION_LABELS(t);

  return (
    <>
      {subtitle ? (
        <p data-testid={`${testid}-subtitle`} className="mb-3 text-[11px] text-[#596b85]">
          {subtitle}
        </p>
      ) : null}
      <StackedBar
        height="h-4"
        segments={destination.segments.map((segment) => ({
          key: segment.key,
          label: labels[segment.key]?.label ?? segment.key,
          className: labels[segment.key]?.className ?? "bg-[#8b5cf6]",
          value: segment.count,
        }))}
      />
      <ul className="mt-3 grid grid-cols-1 gap-1 sm:grid-cols-2">
        {destination.segments.map((segment) => (
          <li key={segment.key}>
            <DetailRowLink
              to={linkFor?.(segment.key)}
              testId={`${testid}-${segment.key}`}
              className="flex items-baseline gap-2 text-xs"
            >
            <span
              aria-hidden
              className={`mt-1 h-2 w-2 shrink-0 rounded-sm ${labels[segment.key]?.className ?? "bg-[#8b5cf6]"}`}
            />
            <span className="min-w-0 flex-1 truncate text-[#08254c]">
              {labels[segment.key]?.label ?? segment.key}
            </span>
            <span className="shrink-0 tabular-nums text-[#08254c]">
              {segment.pct !== undefined ? `${segment.pct}% · ` : ""}
              {count(segment.count)}
            </span>
            </DetailRowLink>
          </li>
        ))}
      </ul>
    </>
  );
}

/** O slug do detalhe do Clean Core em `DIAGNOSTIC_DETAILS` (pedido do David em 19/10/2026). */
const CLEAN_CORE_DETAIL = "clean-core-destination";

/** "Ver objetos": cada objeto customizado com o destino que o card desenha. */
function SeeObjectsLink({ to = CLEAN_CORE_DETAIL }: { to?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      data-testid="clean-core-detail-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See objects")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/** Destino do código Z sob Clean Core. */
function CleanCoreDestinationCard({ sections, snapshots, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const count = (n: number) => formatCount(n);
  // Com decisão do arquiteto, o card conta pela decisão, na base do card WRICEF do
  // Ato 1 (#1712, cartão 3.7); sem nenhuma, fica na classificação automática.
  const frame = wricefDecisions(validation);
  if (frame) {
    return (
      <CleanCoreDecisionCard
        frame={frame}
        envelope={validation?.wricef as SectionEnvelope}
        snapshots={snapshots}
      />
    );
  }
  const destination = cleanCoreDestination(sections);
  const extension = destination.segments.find(
    (segment) => segment.key === "requires_extension",
  )?.count;
  return (
    <Section
      sections={sections}
      name="wricef_catalog"
      title={t("Where the Z code goes under Clean Core")}
      snapshots={snapshots}
      showsSampling={false}
      actions={inRouter ? <SeeObjectsLink /> : undefined}
    >
      <DestinationBody
        linkFor={
          inRouter
            ? (key) => `${CLEAN_CORE_DETAIL}?destination=${encodeURIComponent(key)}`
            : undefined
        }
        subtitle={
          destination.total != null
            ? t("For each of the {{total}} customised objects", {
                total: count(destination.total),
              })
            : t("For each customised object")
        }
        destination={destination}
        testid="clean-core"
      />
      {/*
        A conclusão do ato, e ela diz **menos** que o desenho de propósito.

        O protótipo escreve "nenhum deles modifica o núcleo do sistema" sobre o
        catálogo inteiro; no nosso dado quase metade dos objetos ainda não tem
        decisão, e afirmar sobre eles seria inventar. A frase fala da categoria
        de extensão, onde a afirmação é verdadeira por construção: extensão no
        GROW é key-user in-app ou side-by-side no BTP, e nenhuma toca o núcleo.
      */}
      {extension != null && extension > 0 && (
        <p
          data-testid="clean-core-insight"
          className="mt-3 text-[12px] leading-snug text-[#596b85]"
        >
          {t(
            "{{count}} objects require extension, and extension does not modify the core: it runs key-user in-app or side-by-side on BTP.",
            { count: count(extension) },
          )}
        </p>
      )}
    </Section>
  );
}

/** O slug da lista do Catálogo WRICEF em `DIAGNOSTIC_DETAILS`, a mesma do card do Ato 1. */
const WRICEF_DETAIL = "wricef";

/**
 * O destino do código Z pela decisão do arquiteto (#1712, cartão 3.7).
 *
 * O Marco decidiu em 22/10/2026 que o card segue a decisão do arquiteto, na mesma
 * base do card WRICEF do Ato 1, e que a classificação automática fica só no
 * Architect: na AL5 PS4 o Ato 2 dizia "71 de 153 exigem extensão" com o Ato 1
 * mostrando 59 objetos. O envelope é o do domínio `wricef`, como no Ato 1, e cada
 * decisão abre a lista do Catálogo já filtrada.
 */
function CleanCoreDecisionCard({
  frame,
  envelope,
  snapshots,
}: {
  frame: WricefDecisionFrame;
  envelope: SectionEnvelope;
  snapshots?: DiagnosticTabProps["snapshots"];
}) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const meta = makeDecisionMeta(t);
  const labels = Object.fromEntries(
    Object.entries(meta).map(([token, value]) => [
      token,
      { label: value.label, className: DECISION_BAR[token] ?? "bg-[#e8ebf0]" },
    ]),
  );
  const clean = cleanExtensionCount(frame);
  return (
    <DiagnosticSection
      envelope={envelope}
      title={t("Where the Z code goes under Clean Core")}
      snapshots={snapshots}
      actions={inRouter ? <SeeObjectsLink to={WRICEF_DETAIL} /> : undefined}
    >
      <DestinationBody
        subtitle={t(
          "{{total}} Z/Y objects · technical architect's decision on {{decided}} of {{total}}",
          { total: formatCount(frame.universe), decided: formatCount(frame.decided) },
        )}
        destination={decisionDestination(frame)}
        testid="clean-core"
        labels={labels}
        linkFor={
          inRouter ? (key) => `${WRICEF_DETAIL}?decision=${encodeURIComponent(key)}` : undefined
        }
      />
      {/*
        A conclusão do ato fala só do que o arquiteto pôs em escopo com estratégia
        que não toca o núcleo. O em escopo sem estratégia, ou na classic, não entra:
        afirmar sobre ele seria inventar.
      */}
      {clean > 0 && (
        <p data-testid="clean-core-insight" className="mt-3 text-[12px] leading-snug text-[#596b85]">
          {t(
            "{{count}} objects in scope go to an extension that does not modify the core: key user or developer in-app, or side-by-side on BTP.",
            { count: formatCount(clean) },
          )}
        </p>
      )}
    </DiagnosticSection>
  );
}

/** Destino provável dos processos, em barra única. */
function ProcessDestinationCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  // O desenho escreve "base: 168 scope items" no subtítulo. Sem o denominador
  // à vista, os percentuais da barra não têm sobre o que se apoiar.
  const destination = processDestination(sections);
  const inRouter = useInRouterContext();
  return (
    <Section
      sections={sections}
      name="scope_activation"
      title={t("Likely destination of the processes")}
      snapshots={snapshots}
      showsSampling={false}
      actions={
        inRouter && destination.segments.length > 0 ? (
          <SeeScopeItemsLink to={PROCESS_DESTINATION_DETAIL} />
        ) : undefined
      }
    >
      <DestinationBody
        subtitle={
          destination.total != null
            ? t(
                "Preliminary classification, subject to your team's validation · base: {{total}} scope items",
                { total: count(destination.total) },
              )
            : t("Preliminary classification, subject to your team's validation")
        }
        destination={destination}
        testid="process-destination"
        linkFor={
          inRouter ? (key) => `${PROCESS_DESTINATION_DETAIL}?destination=${key}` : undefined
        }
      />
    </Section>
  );
}

/** Ressalva de leitura, como **texto** do card — nunca `title=`. */
function Caveat({ children }: { children: ReactNode }) {
  return (
    <p
      data-testid="fit-caveat"
      className="mb-3 flex items-start gap-2 rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[11px] leading-snug text-[#596b85]"
    >
      <Info aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-[#7b8799]" />
      <span>{children}</span>
    </p>
  );
}

const MONO_LABEL =
  "font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]";

/** O `t` do i18next, estreitado para o que estes rótulos usam. */
type T = (key: string) => string;

/* -------------------------------------------------------------------------- */
/* 1 · necessidades versus gaps                                                */
/* -------------------------------------------------------------------------- */
/**
 * Gaps por categoria de cobertura (#325).
 *
 * Este card era uma **tabela de detalhe**: item, motivo, Nota SAP, SKU e preço,
 * com rodapé de totais. A nota do apresentador deste ato, versionada em
 * `presenterNotesData.ts`, diz o contrário com todas as letras: *"quantidades e
 * percentuais sim; a lista dos 43 gaps, não. Nada de valores, prazos ou esforço
 * em horas"*. O repo contradizia a si mesmo, e a contradição chegava ao cliente
 * — dinheiro no Ato 2 fura a estrutura dos cinco atos, que guarda o número para
 * o Ato 5 para ele ser lido no contexto do investimento total.
 *
 * O detalhe não se perdeu: a seção continua inteira no payload e no anexo da
 * #690, que é o consumidor certo para ele.
 */
/** O slug do detalhe dos gaps em `DIAGNOSTIC_DETAILS`, relativo ao Diagnóstico (#1479). */
const GAPS_DETAIL = "scope-gaps";

/** "Ver gaps": a lista inteira, um gap por linha, com motivo e nota SAP (#1479). */
function SeeGapsLink() {
  const { t } = useTranslation();
  return (
    <Link
      to={GAPS_DETAIL}
      data-testid="gap-categories-detail-link"
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See gaps")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/** A linha da categoria: link para a lista filtrada quando há roteador, linha simples quando não há. */
function CategoryRow({ to, children }: { to: string | undefined; children: ReactNode }) {
  const layout = "flex items-center gap-3";
  if (!to) return <div className={layout}>{children}</div>;
  return (
    <Link
      to={to}
      className={`${layout} -mx-1 rounded px-1 py-0.5 hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]`}
    >
      {children}
    </Link>
  );
}

function GapCategoriesCard({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const count = (n: number) => formatCount(n);
  const { rows, total } = gapCategories(sections);
  const meta = makeCoverageTypeMeta(t) as Record<
    string,
    { label: string; color: string } | undefined
  >;
  const approved = hasActiveScopeApproval(sections, "scope_gaps");
  const peak = rows.reduce((acc, row) => Math.max(acc, row.count), 0);
  // O card também é desenhado fora de um roteador (anexo, testes): ali ele conta
  // e não navega.
  const inRouter = useInRouterContext();

  return (
    <Section
      sections={sections}
      name="scope_gaps"
      title={t("Gaps by category")}
      subtitle={
        rows.length > 0 && total !== undefined
          ? t("{{count}} points that need a decision in the solution design", {
              count: count(total),
            })
          : undefined
      }
      snapshots={snapshots}
      /* A tabela que saiu daqui consumia `coverage_gaps`, amostra capada em 100,
         e por isso declarava "mostrando 100 de 171". Este card conta
         `by_coverage_type`, agregado sobre a população inteira: declarar amostra
         afirmaria um recorte que não existe. */
      showsSampling={false}
      actions={inRouter && rows.length > 0 ? <SeeGapsLink /> : undefined}
    >
      {rows.length === 0 ? (
        <p className="text-xs text-[#7b8799]">
          {approved
            ? t("No gap in this run — every activated requirement is covered by the standard.")
            : t(
                "No active scope approval yet — approve the scope to see coverage against the standard.",
              )}
        </p>
      ) : (
        <ul data-testid="gap-categories" className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.coverageType}
              data-testid={`gap-category-${row.coverageType}`}
              className="text-xs"
            >
              <CategoryRow
                to={inRouter ? `${GAPS_DETAIL}?category=${row.coverageType}` : undefined}
              >
                <span className="w-44 shrink-0 truncate text-[#08254c]">
                  {/* O builder emite um balde `_UNKNOWN` para gap sem
                      `coverage_type` (`diagnostic_builder.py:957`), e o
                      `makeCoverageTypeMeta` só tem as quatro categorias
                      nomeadas. Sem este caso o token cru ia para a tela do
                      cliente — a tabela que saiu daqui o traduzia. */}
                  {row.coverageType === "_UNKNOWN"
                    ? t("Coverage not recognized")
                    : (meta[row.coverageType]?.label ?? row.coverageType)}
                </span>
                <div className="h-3 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
                  <div
                    className="h-full rounded-sm"
                    style={{
                      width: `${Math.max(2, (row.count / Math.max(1, peak)) * 100)}%`,
                      backgroundColor: meta[row.coverageType]?.color ?? "#6d8fc4",
                    }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right tabular-nums text-[#08254c]">
                  {count(row.count)}
                </span>
              </CategoryRow>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
/* -------------------------------------------------------------------------- */
/* 3 · card sem fonte                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A grid dos scope items que ativaram, e a evidência do que for selecionado.
 *
 * `showsSampling={false}` **de propósito**, contra o default: a linha do envelope
 * diz "mostrando 200 de 614" sobre **todos** os itens, e esta grid mostra só os
 * ativados entre esses 200. Quem contasse os tiles veria 143 e leria 200 de 614.
 * O card declara a própria amostragem, com o total vindo de `summary.activate` —
 * que é o número de ativados na população, não na amostra.
 */
/**
 * As necessidades cobertas pelo padrão, como painel recolhido.
 *
 * Não é card da devolutiva: o desenho aprovado do Ato 2 não o tem. É o
 * utilitário de quem apresenta, para o momento em que o cliente pergunta por um
 * scope item específico e a resposta precisa estar a um clique.
 *
 * Nasce fechado de propósito. Aberto, a grade de itens rouba a leitura dos seis
 * cards que são a devolutiva, que é o que fazia a tela ter onze cards antes de
 * 23/09/2026.
 *
 * A casca é `<details>` nativo em vez de estado em React: ele já abre e fecha
 * sozinho, é acessível por teclado sem nada a mais, e o conteúdo continua no
 * DOM para quem usa busca da página.
 */
function StandardItemsPanel({ sections, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { rows, shown, total } = activatedItems(sections);
  const coverage = evidenceCoverage(sections);
  const selected = rows.find((row) => row.scope_item_id === selectedId) ?? null;
  const approved = hasActiveScopeApproval(sections, "scope_activation");

  return (
    <details data-testid="standard-items-panel" className="group">
      <summary className="cursor-pointer list-none rounded-md border border-[#dbe3ee] bg-white px-4 py-3 text-sm font-medium text-[#08254c] hover:bg-[#f7f9fc]">
        <span className="mr-2 inline-block transition-transform group-open:rotate-90">
          ›
        </span>
        {t("The requirements covered by the standard")}
      </summary>
      <div className="mt-2">
        <Section
          sections={sections}
          name="scope_activation"
          /* O nome já está no gatilho do acordeão, logo acima. Repeti-lo aqui
             dava o mesmo título duas vezes na mesma dobra; o card diz o que
             tem dentro. */
          title={t("Activated scope items")}
          snapshots={snapshots}
          showsSampling={false}
        >
          {rows.length === 0 ? (
            <p className="text-xs text-[#7b8799]">
              {approved
                ? t("No scope item activated in this run.")
                : t(
                    "No active scope approval yet — approve the scope to see the requirements covered by the standard.",
                  )}
            </p>
          ) : (
            <>
              {total != null && shown < total && (
                <div
                  data-testid="standard-items-sampled"
                  className={cn("mb-3", MONO_LABEL)}
                >
                  {t("Showing {{shown}} of {{total}} activated scope items", {
                    shown,
                    total,
                  })}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                {rows.map((row) => (
                  <button
                    key={row.scope_item_id}
                    type="button"
                    data-testid={`scope-tile-${row.scope_item_id}`}
                    aria-pressed={row.scope_item_id === selectedId}
                    onClick={() =>
                      setSelectedId(
                        row.scope_item_id === selectedId
                          ? null
                          : (row.scope_item_id ?? null),
                      )
                    }
                    className={cn(
                      "rounded-lg border px-3 py-2.5 text-left transition-colors",
                      row.scope_item_id === selectedId
                        ? "border-[#08254c] bg-[#f2f4f7]"
                        : "border-[#e8ebf0] hover:border-[#b2bccb]",
                    )}
                  >
                    <div className="font-mono text-xs font-bold text-[#27a155]">
                      {row.scope_item_id}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-snug text-[#596b85]">
                      {row.scope_item_name ?? NO_VALUE}
                    </div>
                  </button>
                ))}
              </div>

              <EvidencePanel
                item={selected}
                version={activationVersion(sections)}
              />

              {coverage && (
                <Caveat>
                  {t(
                    "Usage evidence is available for {{withTcodes}} of {{total}} scope items — the rest have no catalog transactions to cross, so nothing can be said about their usage.",
                    {
                      withTcodes: coverage.itemsWithKeyTcodes,
                      total: coverage.total,
                    },
                  )}
                </Caveat>
              )}
            </>
          )}
        </Section>
      </div>
    </details>
  );
}

/**
 * O que disparou o item selecionado.
 *
 * ⚠️ O rótulo da primeira linha é **"tables and fields observed"**, nunca
 * "transactions observed". O design pedia transações; o dado é tabela e predicado
 * de campo. É o critério de aceite 5 da #466, e há um teste de cópia que falha se
 * alguém trocar o rótulo.
 */
function EvidencePanel({
  item,
  version,
}: {
  item: ActivatedItem | null;
  version?: string;
}) {
  const { t } = useTranslation();

  if (!item) {
    return (
      <div
        data-testid="evidence-panel-empty"
        className="mt-4 rounded-lg border border-[#e8ebf0] bg-[#fafbfc] px-5 py-4 text-xs text-[#7b8799]"
      >
        {t("Select an item to see the evidence behind its classification.")}
      </div>
    );
  }

  const confidence = makeConfidenceMeta(t)[item.confidence ?? ""];
  const tier = makeTierMeta(t)[item.tier ?? ""];
  const quality = makeEvidenceQualityMeta(t)[item.evidence.quality ?? ""];

  return (
    <div
      data-testid="evidence-panel"
      className="mt-4 rounded-lg border border-[#e8ebf0] bg-[#fafbfc] px-5 py-4"
    >
      <dl className="space-y-2.5">
        <EvidenceRow label={t("tables and fields observed")}>
          {item.evidence.tables.length > 0 ? (
            <span className="font-mono text-xs text-[#596b85]">
              {item.evidence.tables.join(" · ")}
            </span>
          ) : (
            <span className="text-xs text-[#7b8799]">
              {t("Activated by a rule with no table signal.")}
            </span>
          )}
        </EvidenceRow>

        <EvidenceRow label={t("transactions observed in use")}>
          <ObservedTcodes evidence={item.evidence} />
        </EvidenceRow>

        {item.evidence.capabilities.length > 0 && (
          <EvidenceRow label={t("capability to S/4 target")}>
            <ul className="space-y-1">
              {item.evidence.capabilities.map((capability, index) => (
                <li
                  key={`${capability.capability ?? index}`}
                  className="text-xs text-[#596b85]"
                >
                  <span>{capability.capability ?? NO_VALUE}</span>
                  <span className="mx-1.5 text-[#a3adbb]">→</span>
                  <span className="text-[#08254c]">
                    {capability.s4Target ?? NO_VALUE}
                  </span>
                  {capability.tables.length > 0 && (
                    <span className="ml-2 font-mono text-[10px] text-[#a3adbb]">
                      {capability.tables.join(", ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </EvidenceRow>
        )}

        <EvidenceRow label={t("confidence")}>
          {confidence ? (
            <Pill tone={confidence.tone}>{confidence.label}</Pill>
          ) : (
            <span className="text-xs text-[#7b8799]">
              {NO_VALUE} {t("Confidence not declared for this rule.")}
            </span>
          )}
        </EvidenceRow>

        <EvidenceRow label={t("tier")}>
          {tier ? (
            <Pill tone={tier.tone}>{tier.label}</Pill>
          ) : (
            <span className="text-xs text-[#7b8799]">{NO_VALUE}</span>
          )}
        </EvidenceRow>

        {quality && (
          <EvidenceRow label={t("evidence quality")}>
            <Pill tone={quality.tone}>{quality.label}</Pill>
          </EvidenceRow>
        )}

        <EvidenceRow label={t("rule version")}>
          <span className="font-mono text-xs text-[#596b85]">
            {version ?? NO_VALUE}
          </span>
        </EvidenceRow>
      </dl>

      {item.evidence.truncated && (
        <p
          data-testid="evidence-truncated"
          className="mt-3 text-[11px] italic text-[#7b8799]"
        >
          {t(
            "This item has more evidence than the payload carries — the list above is cut.",
          )}
        </p>
      )}
    </div>
  );
}

function EvidenceRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className={cn("shrink-0 sm:w-52", MONO_LABEL)}>{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/**
 * Os **quatro** estados de `observed_tcodes`, cada um com a sua copy.
 *
 * | Estado | Significa |
 * |---|---|
 * | chave ausente | run anterior ao DIAG.8 — não dá para saber |
 * | `[]` + `no_key_tcodes` | item do tier RAG: não havia catálogo a cruzar |
 * | `[]` + `no_usage` | a run não trouxe uso a comparar |
 * | `[…]` | uso medido |
 *
 * Colapsá-los em "sem uso" afirmaria sobre o cliente algo que o dado não diz em
 * três dos quatro casos.
 */
function ObservedTcodes({ evidence }: { evidence: ActivatedItem["evidence"] }) {
  const { t } = useTranslation();
  const source = makeObservedSourceMeta(t)[evidence.observedSource ?? ""];

  if (evidence.observedTcodes == null) {
    return (
      <span data-testid="observed-unknown" className="text-xs text-[#7b8799]">
        {t("This run did not record observed usage.")}
      </span>
    );
  }

  if (evidence.observedTcodes.length === 0) {
    return (
      <span data-testid="observed-none" className="text-xs text-[#7b8799]">
        {source?.explanation ?? t("Nothing recorded for this item.")}
      </span>
    );
  }

  return (
    <span
      data-testid="observed-tcodes"
      className="font-mono text-xs text-[#27a155]"
    >
      {evidence.observedTcodes.join(" · ")}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* 0 · veredito, cobertura e aderência (vieram do Ato 1 na #852)               */
/* -------------------------------------------------------------------------- */



